import { useState, useCallback } from 'react'
import { authFetch } from '../utils/authFetch'
import { Order, ProcessOptions } from '../types'

interface OrderStats {
  totalRevenue: number
  widdeCount: number
  widdeRevenue: number
  influencedRevenue: number
  /** Influência por visualização (viu vídeo → comprou), independente de produto. */
  viewInfluencedCount?: number
  viewInfluencedRevenue?: number
  directCount: number
}

interface UseOrdersReturn {
  orders: Order[]
  allOrders: Order[]
  isLoading: boolean
  error: string | null
  stats: OrderStats | null
  /** Pedidos da company na fonte dela: `platform_orders` quando ela tem integração de pedidos, senão `ga_orders`. */
  fetchOrders: (companyKey: string, startDate?: string, endDate?: string) => Promise<void>
  startFullProcess: (options: ProcessOptions) => Promise<{ sessionId: string }>
}

export function useOrders(): UseOrdersReturn {
  const [orders, setOrders] = useState<Order[]>([])
  const [allOrders, setAllOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<OrderStats | null>(null)

  const fetchOrders = useCallback(async (companyKey: string, startDate?: string, endDate?: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams()
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      const qs = params.toString()
      const response = await authFetch(`/api/companies/${encodeURIComponent(companyKey)}/orders${qs ? `?${qs}` : ''}`)

      if (!response.ok) {
        throw new Error(`Erro ao buscar pedidos: ${response.statusText}`)
      }

      const data: Order[] = await response.json()
      setAllOrders(data)

      // Filtrar pedidos com widdeUid e transactionId validos (como na versao original)
      const filtered = data.filter(o =>
        o.transactionId && !/^\(.*\)$/.test(o.transactionId) &&
        o.widdeUid && o.widdeUid !== '(not set)'
      )
      setOrders(filtered)

      // Calcular estatisticas
      const allTotal = data.reduce((sum, o) => sum + (o.purchaseRevenue || 0), 0)
      const widdeRevenue = filtered.reduce((sum, o) => sum + (o.purchaseRevenue || 0), 0)
      const influencedRevenue = filtered
        .filter(o => o.widdeInfluenciou)
        .reduce((sum, o) => sum + (o.influencedItemRevenue || 0), 0)
      const directCount = filtered.filter(o => o.widdeDireto).length
      const viewInfluencedOrders = filtered.filter(o => o.viewInfluenced)
      const viewInfluencedRevenue = viewInfluencedOrders
        .reduce((sum, o) => sum + (o.purchaseRevenue || 0), 0)

      setStats({
        totalRevenue: allTotal,
        widdeCount: filtered.filter(o => o.widdeInfluenciou).length,
        widdeRevenue,
        influencedRevenue,
        viewInfluencedCount: viewInfluencedOrders.length,
        viewInfluencedRevenue,
        directCount,
      })
    } catch (err: any) {
      console.error('Erro ao buscar pedidos:', err)
      setError(err.message || 'Erro ao buscar pedidos')
      setOrders([])
      setAllOrders([])
      setStats(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const startFullProcess = useCallback(async (options: ProcessOptions): Promise<{ sessionId: string }> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch('/api/process-full', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erro ao iniciar processamento')
      }

      const data = await response.json()

      if (!data.success || !data.sessionId) {
        throw new Error('Resposta inválida do servidor')
      }

      return { sessionId: data.sessionId }
    } catch (err: any) {
      console.error('Erro ao iniciar processamento:', err)
      setError(err.message || 'Erro ao iniciar processamento')
      setIsLoading(false)
      throw err
    }
  }, [])

  return {
    orders,
    allOrders,
    isLoading,
    error,
    stats,
    fetchOrders,
    startFullProcess,
  }
}
